import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { Gavel, Refresh } from '@mui/icons-material'
import type { AxiosError } from 'axios'
import { acceptNda, NDA_CURRENT_QUERY_KEY } from '@api/endpoints/nda'
import type { NdaStatus, NdaStaleVersionError } from '@api/types/nda'
import { NDA_VERSION_STALE_CODE } from '@api/types/nda'
import { useNdaStore } from '@store/ndaStore'
import { useAuthStore } from '@store/authStore'

interface NdaAgreementScreenProps {
  /** The agreement in force, once loaded. Absent while it is still being fetched. */
  status?: NdaStatus
  /** A fetch of the agreement is in flight. */
  loading: boolean
  /** The agreement could not be loaded. */
  loadError: boolean
  /** Ask for the agreement again. */
  onReload: () => void
}

/**
 * The blocking screen: the agreement, and the one way past it.
 *
 * This is a full-viewport page rather than a dialog because it is a legal document that people
 * are expected to actually read — it needs room and its own scroll region, and squeezing it into
 * a modal would encourage exactly the skim-and-click it exists to prevent.
 *
 * The same screen serves first acceptance and re-acceptance. Accepting is per version, so a
 * published v2 puts a user who signed v1 right back here; there is nothing different to show
 * them, only different words.
 */
export function NdaAgreementScreen({ status, loading, loadError, onReload }: NdaAgreementScreenProps) {
  const queryClient = useQueryClient()
  const clearBlocked = useNdaStore(state => state.clearBlocked)
  const logout = useAuthStore(state => state.logout)

  const [agreed, setAgreed] = useState(false)
  const [staleNotice, setStaleNotice] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement | null>(null)

  const version = status?.version ?? null

  // A new version means new words. Take back the tick and send the reader to the top of the text
  // they have not read, rather than leaving a checked box sitting under a changed document.
  useEffect(() => {
    setAgreed(false)
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0
    }
  }, [version])

  const accept = useMutation({
    mutationFn: (v: number) => acceptNda(v),
    // Accepting twice would be harmless but a retried 409 is noise; the failure cases here are
    // all answers, not accidents.
    retry: false,
    onSuccess: () => {
      setFailure(null)
      setStaleNotice(false)
      clearBlocked()
      // Open the gate now, so accepting does not sit on a spinner waiting for a round trip we
      // already know the answer to. This is a convenience, not a record: the invalidate below
      // immediately asks the server, and if it disagrees — a newer version landed in that same
      // instant — the gate closes again on its answer, not ours.
      queryClient.setQueryData(NDA_CURRENT_QUERY_KEY, (previous?: NdaStatus) =>
        previous ? { ...previous, accepted: true } : previous
      )
      // Ask again, and give everything that failed behind the closed gate a chance to run now
      // that it is open.
      queryClient.invalidateQueries()
    },
    onError: (error: AxiosError<NdaStaleVersionError>) => {
      if (error.response?.status === 409 && error.response.data?.error === NDA_VERSION_STALE_CODE) {
        // The text changed under them. Reload it and ask again — recording a signature against
        // words they never saw is the one outcome worth refusing outright.
        setStaleNotice(true)
        setFailure(null)
        setAgreed(false)
        onReload()
        return
      }

      setStaleNotice(false)
      setFailure(
        error.response?.data?.message ||
          'That could not be recorded just now. Please check your connection and try again.'
      )
    },
  })

  const canAccept = agreed && typeof version === 'number' && !accept.isPending && !loading

  const publishedAt = status?.publishedAt ? new Date(status.publishedAt) : null

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        p: { xs: 1.5, sm: 3 },
      }}
    >
      <Paper
        elevation={3}
        sx={{
          width: '100%',
          maxWidth: 900,
          // Cap to the viewport so the body scrolls inside the sheet instead of the page
          // scrolling away from the accept button.
          maxHeight: { xs: 'calc(100dvh - 24px)', sm: 'calc(100dvh - 48px)' },
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 4 }, pt: { xs: 2.5, sm: 3.5 }, pb: 2 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
            <Gavel color="primary" />
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, flex: 1 }}>
              {status?.title || 'Non-Disclosure Agreement'}
            </Typography>
            {typeof version === 'number' && (
              <Chip size="small" label={`Version ${version}`} variant="outlined" />
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary">
            This app is in private testing. Please read and accept the agreement below to continue.
            {publishedAt && !Number.isNaN(publishedAt.getTime())
              ? ` Published ${publishedAt.toLocaleDateString()}.`
              : ''}
          </Typography>

          {staleNotice && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              The agreement was updated while this page was open. The new text is below — please
              read it and accept again.
            </Alert>
          )}

          {failure && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {failure}
            </Alert>
          )}

          {loadError && !status && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              action={
                <Button color="inherit" size="small" startIcon={<Refresh />} onClick={onReload}>
                  Retry
                </Button>
              }
            >
              The agreement could not be loaded. Check your connection and try again.
            </Alert>
          )}
        </Box>

        <Divider />

        {/* The document. Its own scroll region so long text stays readable and the controls
            below never leave the screen. */}
        <Box
          ref={scrollRef}
          sx={{
            flex: 1,
            minHeight: 120,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            px: { xs: 2, sm: 4 },
            py: 3,
            backgroundColor: 'background.paper',
          }}
        >
          {status?.body ? (
            <Typography
              component="pre"
              sx={{
                // Authored as plain text in the admin panel: keep the author's line breaks and
                // spacing, wrap long lines, and render it as text rather than as markup.
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontFamily: 'inherit',
                fontSize: { xs: '0.9rem', sm: '0.95rem' },
                lineHeight: 1.75,
                m: 0,
              }}
            >
              {status.body}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              {loading ? (
                <CircularProgress size={28} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  The agreement is not available right now.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Divider />

        <Box sx={{ px: { xs: 2, sm: 4 }, py: { xs: 2, sm: 2.5 } }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={agreed}
                onChange={event => setAgreed(event.target.checked)}
                disabled={typeof version !== 'number' || accept.isPending}
              />
            }
            label="I have read and agree to the terms of this non-disclosure agreement."
          />

          <Stack
            direction={{ xs: 'column-reverse', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 1.5, justifyContent: 'flex-end' }}
          >
            {/* An escape hatch. Somebody signed in to the wrong account should not have to accept
                an agreement to get back out of it. */}
            <Button color="inherit" onClick={() => logout()} disabled={accept.isPending}>
              Sign out
            </Button>
            <Button
              variant="contained"
              size="large"
              disabled={!canAccept}
              onClick={() => {
                if (typeof version === 'number') {
                  accept.mutate(version)
                }
              }}
              startIcon={accept.isPending ? <CircularProgress size={18} color="inherit" /> : undefined}
            >
              {accept.isPending ? 'Recording…' : 'Accept and continue'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  )
}
